import React, { useState, useEffect } from 'react';
import AuthorFields from '../components/AuthorFields';
import CitationResult from '../components/CitationResult';
import { 
  formatAuthorsForCitation, 
  formatAuthorsForParenthetical, 
  formatAuthorsForReference, 
  formatEditionForReference, 
  formatURL, 
  formatDOI, 
  formatEditorsForReference 
} from '../utils/citationHelpers';

function ChapterForm() {
  const [authors, setAuthors] = useState([]);
  const [authorType, setAuthorType] = useState('both');
  const [editors, setEditors] = useState([]);
  const [editorType, setEditorType] = useState('both');
  const [editorRole, setEditorRole] = useState('editor');
  const [formData, setFormData] = useState({
    chapterTitle: '',
    bookTitle: '',
    year: '',
    edition: '',
    pages: '',
    publisher: '',
    urlOrDoi: ''
  });
  const [result, setResult] = useState(null);
  const [liveResult, setLiveResult] = useState(null);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Genera una vista previa en tiempo real
  useEffect(() => {
    if (authors.length > 0 && formData.chapterTitle && formData.bookTitle && formData.year) {
      generatePreview();
    } else {
      setLiveResult(null);
    }
  }, [authors, editors, formData, editorRole]);

  const generatePreview = () => {
    // Verificar si hay autores institucionales con siglas
    const institutionalAuthors = authors.filter(
      author => author.type === 'institutional' && author.abbreviation
    );

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;

    if (institutionalAuthors.length === 1 && (authorType === 'institutional' || authorType === 'both')) {
      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${institutionalAuthors[0].name} (${institutionalAuthors[0].abbreviation}, ${formData.year})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${institutionalAuthors[0].name} [${institutionalAuthors[0].abbreviation}], ${formData.year})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${institutionalAuthors[0].abbreviation} (${formData.year})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${institutionalAuthors[0].abbreviation}, ${formData.year})`;
    } else {
      // Caso normal sin autor institucional con sigla o múltiples autores
      const authorsForNarrative = formatAuthorsForCitation(authors, authorType);
      const authorsForParenthetical = formatAuthorsForParenthetical(authors, authorType);
      const authorsForNarrativeSubsequent = formatAuthorsForCitation(authors, authorType, false);
      const authorsForParentheticalSubsequent = formatAuthorsForParenthetical(authors, authorType, false);

      narrativeCitation = `${authorsForNarrative} (${formData.year})`;
      parentheticalCitation = `(${authorsForParenthetical}, ${formData.year})`;
      narrativeSubsequent = `${authorsForNarrativeSubsequent} (${formData.year})`;
      parentheticalSubsequent = `(${authorsForParentheticalSubsequent}, ${formData.year})`;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const authorsForReference = formatAuthorsForReference(authors, authorType);
    const editorsForReference = editors.length > 0 ? formatEditorsForReference(editors, editorType, editorRole) : '';
    const edition = formatEditionForReference(formData.edition);

    let formattedUrlOrDoi = '';
    if (formData.urlOrDoi) {
      // Comprobar si es un DOI por patrón o prefijo
      if (formData.urlOrDoi.toLowerCase().includes('doi') || /^10\.\d{4,}\/\S+$/.test(formData.urlOrDoi)) {
        formattedUrlOrDoi = formatDOI(formData.urlOrDoi);
      } else {
        formattedUrlOrDoi = formatURL(formData.urlOrDoi);
      }
    }

    let referenceText = `${authorsForReference} (${formData.year}). ${formData.chapterTitle}. En `;
    
    if (editorsForReference) {
      referenceText += `${editorsForReference}, `;
    }
    
    referenceText += `<em>${formData.bookTitle}</em>`;

    // Combinar edición y páginas en un solo paréntesis si ambos existen
    if (edition && formData.pages) {
      referenceText += ` (${edition.substring(1, edition.length - 1)}, pp. ${formData.pages})`;
    } else if (edition) {
      referenceText += ` ${edition}`;
    } else if (formData.pages) {
      referenceText += ` (pp. ${formData.pages})`;
    }

    if (formData.publisher) {
      referenceText += `. ${formData.publisher}`;
    }
    
    if (formattedUrlOrDoi) {
      referenceText += `. ${formattedUrlOrDoi}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setLiveResult({ citation, reference });
  };

  const generateCitation = () => {
    if (authors.length === 0 || !formData.chapterTitle || !formData.bookTitle || !formData.year) {
      alert('Generador APA, séptima edición dice:\n\nPor favor, completa todos los campos obligatorios.');
      return;
    }

    // Verificar si hay autores institucionales con siglas
    const institutionalAuthors = authors.filter(
      author => author.type === 'institutional' && author.abbreviation
    );

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;

    if (institutionalAuthors.length === 1 && (authorType === 'institutional' || authorType === 'both')) {
      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${institutionalAuthors[0].name} (${institutionalAuthors[0].abbreviation}, ${formData.year})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${institutionalAuthors[0].name} [${institutionalAuthors[0].abbreviation}], ${formData.year})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${institutionalAuthors[0].abbreviation} (${formData.year})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${institutionalAuthors[0].abbreviation}, ${formData.year})`;
    } else {
      // Caso normal sin autor institucional con sigla o múltiples autores
      const authorsForNarrative = formatAuthorsForCitation(authors, authorType);
      const authorsForParenthetical = formatAuthorsForParenthetical(authors, authorType);
      const authorsForNarrativeSubsequent = formatAuthorsForCitation(authors, authorType, false);
      const authorsForParentheticalSubsequent = formatAuthorsForParenthetical(authors, authorType, false);

      narrativeCitation = `${authorsForNarrative} (${formData.year})`;
      parentheticalCitation = `(${authorsForParenthetical}, ${formData.year})`;
      narrativeSubsequent = `${authorsForNarrativeSubsequent} (${formData.year})`;
      parentheticalSubsequent = `(${authorsForParentheticalSubsequent}, ${formData.year})`;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const authorsForReference = formatAuthorsForReference(authors, authorType);
    const editorsForReference = editors.length > 0 ? formatEditorsForReference(editors, editorType, editorRole) : '';
    const edition = formatEditionForReference(formData.edition);

    let formattedUrlOrDoi = '';
    if (formData.urlOrDoi) {
      // Comprobar si es un DOI por patrón o prefijo
      if (formData.urlOrDoi.toLowerCase().includes('doi') || /^10\.\d{4,}\/\S+$/.test(formData.urlOrDoi)) {
        formattedUrlOrDoi = formatDOI(formData.urlOrDoi);
      } else {
        formattedUrlOrDoi = formatURL(formData.urlOrDoi);
      }
    }

    let referenceText = `${authorsForReference} (${formData.year}). ${formData.chapterTitle}. En `;
    
    if (editorsForReference) {
      referenceText += `${editorsForReference}, `;
    }
    
    referenceText += `<em>${formData.bookTitle}</em>`;

    // Combinar edición y páginas en un solo paréntesis si ambos existen
    if (edition && formData.pages) {
      referenceText += ` (${edition.substring(1, edition.length - 1)}, pp. ${formData.pages})`;
    } else if (edition) {
      referenceText += ` ${edition}`;
    } else if (formData.pages) {
      referenceText += ` (pp. ${formData.pages})`;
    }

    if (formData.publisher) {
      referenceText += `. ${formData.publisher}`;
    }
    
    if (formattedUrlOrDoi) {
      referenceText += `. ${formattedUrlOrDoi}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setResult({ citation, reference });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-4 sm:mb-8">
        <h1 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2 dark:text-gray-100">Capítulo de libro</h1>
        <p className="text-gray-600 dark:text-gray-400">Capítulos dentro de libros editados</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 sm:gap-8">
        <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-5 dark:bg-gray-800 dark:border-gray-700">
          <div className="space-y-3">
            <AuthorFields
              authors={authors}
              setAuthors={setAuthors}
              authorType={authorType}
              setAuthorType={setAuthorType}
              label="Autores del capítulo"
            />

            <div>
              <label htmlFor="chapterTitle" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Título del capítulo *
              </label>
              <input
                type="text"
                id="chapterTitle"
                name="chapterTitle"
                value={formData.chapterTitle}
                onChange={handleInputChange}
                className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm sm:text-base"
                required
              />
            </div>

            <AuthorFields
              authors={editors}
              setAuthors={setEditors}
              authorType={editorType}
              setAuthorType={setEditorType}
              label="Editores del libro"
              showAuthorType={true}
            />

            <div>
              <label htmlFor="editorRole" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Tipo de editor
              </label>
              <select
                id="editorRole"
                value={editorRole}
                onChange={(e) => setEditorRole(e.target.value)}
                className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm sm:text-base"
              >
                <option value="editor">Editor</option>
                <option value="compilador">Compilador</option>
                <option value="coordinador">Coordinador</option>
              </select>
            </div>

            <div>
              <label htmlFor="bookTitle" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Título del libro *
              </label>
              <input
                type="text"
                id="bookTitle"
                name="bookTitle"
                value={formData.bookTitle}
                onChange={handleInputChange}
                className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm sm:text-base"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-4">
              <div>
                <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Año de publicación *
                </label>
                <input
                  type="number"
                  id="year"
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                  className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm sm:text-base"
                  required
                />
              </div>
              <div>
                <label htmlFor="edition" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Edición
                </label>
                <input
                  type="number"
                  id="edition"
                  name="edition"
                  value={formData.edition}
                  onChange={handleInputChange}
                  min="1"
                  className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm sm:text-base"
                  placeholder="Número"
                />
              </div>
            </div>

            <div>
              <label htmlFor="pages" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Páginas (ej: 15-30)
              </label>
              <input
                type="text"
                id="pages"
                name="pages"
                value={formData.pages}
                onChange={handleInputChange}
                className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm sm:text-base"
                placeholder="15-30"
              />
            </div>

            <div>
              <label htmlFor="publisher" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Editorial *
              </label>
              <input
                type="text"
                id="publisher"
                name="publisher"
                value={formData.publisher}
                onChange={handleInputChange}
                className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm sm:text-base"
                required
              />
            </div>

            <div>
              <label htmlFor="urlOrDoi" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                URL o DOI
              </label>
              <input
                type="text"
                id="urlOrDoi"
                name="urlOrDoi"
                value={formData.urlOrDoi}
                onChange={handleInputChange}
                className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm sm:text-base"
                placeholder="URL o DOI (10.xxxx/xxxxx)"
              />
              <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                URL completa, enlace sin https://, o DOI con/sin prefijo https://doi.org/
              </p>
            </div>

            <button
              type="button"
              onClick={generateCitation}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              Generar cita y referencia
            </button>
          </div>
        </div>

        <div className="space-y-4 sm:space-y-6">
          {!result && liveResult && (
            <CitationResult
              citation={liveResult.citation}
              reference={liveResult.reference}
              livePreview={true}
            />
          )}
          <CitationResult
            citation={result?.citation}
            reference={result?.reference}
          />
        </div>
      </div>
    </div>
  );
}

export default ChapterForm;